/**
 * SmartLinkDocumentModal (iter1401 · iter1404 Phase 4b)
 *
 * Composite settlement wizard that links a bank-feed transaction to
 * one or many open documents.
 *
 * Endpoints:
 *   GET  /api/bank-feeds/linkable-documents/{tx_id}
 *   GET  /api/bank-feeds/settlement-scenarios
 *   POST /api/bank-feeds/link-and-settle
 *
 * iter1404 Phase 4b polish:
 *   • Two-column layout (candidates + persistent summary panel)
 *   • Filter chip row (amount, date, currency, counterparty, ±tolerance)
 *   • Sort selector (best match / amount / date / counterparty)
 *   • Softer, gradient header + roomier spacing + progress rail on the
 *     match total
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Search, Loader2, FileText, Receipt, CreditCard, Building2,
  Banknote, TrendingUp, ShoppingCart, Repeat, Users, Landmark,
  AlertCircle, Link2, X, Filter, ArrowUpDown, RotateCcw,
  Calendar, Coins, Users2, Target, DollarSign, BookCheck,
} from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { pickRateDirection, txToDocCcy } from '../../lib/fxResolver';

const API = process.env.REACT_APP_BACKEND_URL;

const EXCESS_OPTIONS = {
  'invoice/inflow': [
    { key: 'customer_advance', label: 'Customer overpaid - hold as Customer Advance (apply to their next invoice)' },
    { key: 'invoice_adjustment', label: 'Invoice was understated - add the difference to the invoice' },
    { key: 'bank_charges_fx', label: 'Bank / FX difference - post to a GL', needsGl: true },
  ],
  'bill/outflow': [
    { key: 'supplier_advance', label: 'Paid the supplier too much - hold as Supplier Advance (apply to their next bill)' },
    { key: 'bill_adjustment', label: 'Bill was understated - add the difference to the bill' },
    { key: 'bank_charges_fx', label: 'Bank charges / FX difference - post to a GL', needsGl: true },
  ],
  'loan/outflow': [
    { key: 'interest_penalty', label: 'Extra is interest / penalty charged by the lender' },
    { key: 'loan_overpayment', label: 'Overpaid the lender - refundable (held as advance)' },
    { key: 'bank_charges_fx', label: 'Bank charges / FX difference - post to a GL', needsGl: true },
  ],
  'investment/inflow': [
    { key: 'investment_income', label: 'Extra is interest / gain on the investment' },
    { key: 'bank_charges_fx', label: 'Bank / FX difference - post to a GL', needsGl: true },
  ],
  'recorded_entry/inflow': [
    { key: 'bank_charges_fx', label: 'Difference to a GL (FX gain, refunds, charges)', needsGl: true },
    { key: 'customer_advance', label: 'Extra received - hold as Customer Advance' },
  ],
  'recorded_entry/outflow': [
    { key: 'bank_charges_fx', label: 'Difference to a GL (bank charges, FX loss)', needsGl: true },
    { key: 'supplier_advance', label: 'Extra paid - hold as Supplier Advance' },
  ],
};

const DOC_META = {
  recorded_entry:     { label: 'Already in books',    icon: BookCheck,    color: 'text-emerald-800 bg-emerald-50 border-emerald-300' },
  invoice:            { label: 'Invoices',           icon: FileText,     color: 'text-blue-700 bg-blue-50 border-blue-200' },
  bill:               { label: 'Bills',              icon: Receipt,      color: 'text-orange-700 bg-orange-50 border-orange-200' },
  fixed_asset:        { label: 'Fixed Assets',       icon: Building2,    color: 'text-purple-700 bg-purple-50 border-purple-200' },
  loan:               { label: 'Loans',              icon: Banknote,     color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  investment:         { label: 'Investments',        icon: TrendingUp,   color: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
  prepayment:         { label: 'Prepayments',        icon: CreditCard,   color: 'text-cyan-700 bg-cyan-50 border-cyan-200' },
  deferred_revenue:   { label: 'Deferred Revenue',   icon: Repeat,       color: 'text-teal-700 bg-teal-50 border-teal-200' },
  payroll_run:        { label: 'Payroll',            icon: Users,        color: 'text-rose-700 bg-rose-50 border-rose-200' },
  statutory_payable:  { label: 'Statutory Payables', icon: Landmark,     color: 'text-red-700 bg-red-50 border-red-200' },
  manual_journal:     { label: 'Manual Journals',    icon: FileText,     color: 'text-slate-700 bg-slate-50 border-slate-200' },
  other_receipt:      { label: 'Other Receipts',     icon: Receipt,      color: 'text-green-700 bg-green-50 border-green-200' },
  other_payment:      { label: 'Other Payments',     icon: ShoppingCart, color: 'text-amber-700 bg-amber-50 border-amber-200' },
};

// ─── Small chip primitive ─────────────────────────────────────
function Chip({ icon: Icon, label, value, onClear, testId, children }) {
  return (
    <div
      className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-800"
      data-testid={testId}>
      {Icon && <Icon className="w-3 h-3" />}
      <span className="font-medium">{label}</span>
      <span className="text-blue-700">{value || children}</span>
      {onClear && (
        <button onClick={onClear} className="hover:text-blue-950 transition"
                aria-label="Clear" data-testid={`${testId}-clear`}>
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}


// ─── Filter Popover ────────────────────────────────────────────
function FilterPopover({ filters, onChange, currencies }) {
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState(filters);

  useEffect(() => setStaged(filters), [filters, open]);

  const apply = () => { onChange(staged); setOpen(false); };
  const reset = () => {
    const empty = {
      amount_min: '', amount_max: '', date_from: '', date_to: '',
      currency: '', counterparty: '', tolerance_pct: '',
    };
    setStaged(empty); onChange(empty); setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1"
                data-testid="sl-filter-btn">
          <Filter className="w-3.5 h-3.5" /> Filters
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" side="bottom" align="end">
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">
            Refine candidates
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Min amount</Label>
              <Input type="number" step="0.01" className="h-7 text-xs"
                     value={staged.amount_min || ''}
                     onChange={(e) => setStaged({ ...staged, amount_min: e.target.value })}
                     data-testid="sl-filter-amount-min" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Max amount</Label>
              <Input type="number" step="0.01" className="h-7 text-xs"
                     value={staged.amount_max || ''}
                     onChange={(e) => setStaged({ ...staged, amount_max: e.target.value })}
                     data-testid="sl-filter-amount-max" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Date from</Label>
              <Input type="date" className="h-7 text-xs"
                     value={staged.date_from || ''}
                     onChange={(e) => setStaged({ ...staged, date_from: e.target.value })}
                     data-testid="sl-filter-date-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Date to</Label>
              <Input type="date" className="h-7 text-xs"
                     value={staged.date_to || ''}
                     onChange={(e) => setStaged({ ...staged, date_to: e.target.value })}
                     data-testid="sl-filter-date-to" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Currency</Label>
              <Select value={staged.currency || 'ANY'}
                      onValueChange={(v) => setStaged({ ...staged, currency: v === 'ANY' ? '' : v })}>
                <SelectTrigger className="h-7 text-xs" data-testid="sl-filter-currency">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ANY">Any</SelectItem>
                  {(currencies || []).map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">
                Tolerance ± %
              </Label>
              <Input type="number" step="1" min="0" className="h-7 text-xs"
                     value={staged.tolerance_pct || ''}
                     placeholder="e.g. 5"
                     onChange={(e) => setStaged({ ...staged, tolerance_pct: e.target.value })}
                     data-testid="sl-filter-tolerance" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">
                Counterparty
              </Label>
              <Input className="h-7 text-xs"
                     placeholder="Customer, vendor, lender…"
                     value={staged.counterparty || ''}
                     onChange={(e) => setStaged({ ...staged, counterparty: e.target.value })}
                     data-testid="sl-filter-counterparty" />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={reset}
                    data-testid="sl-filter-reset">
              <RotateCcw className="w-3 h-3 mr-1" /> Reset
            </Button>
            <Button size="sm" className="h-7 text-xs" onClick={apply}
                    data-testid="sl-filter-apply">
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}


function DocCard({ doc, tx, selected, onSelect, allocation, onUpdateAlloc, scenarios, fxRate }) {
  const meta = DOC_META[doc.doc_type] || DOC_META.other_payment;
  const Icon = meta.icon;
  const currencyMismatch = doc.currency && tx.currency && doc.currency !== tx.currency;
  const proximity = Math.abs((doc.outstanding ?? doc.total_amount ?? 0) - Math.abs(tx.amount || 0));
  const closeMatch = proximity <= Math.max(1, Math.abs(tx.amount || 0)) * 0.05;

  return (
    <div
      onClick={(e) => {
        // iter1411 · Make the whole card toggle selection so users don't
        // have to hit the checkbox. Ignore clicks that originated inside
        // interactive children (inputs, buttons, links, popovers).
        const t = e.target;
        if (t.closest && t.closest('input, textarea, button, select, option, label, [role="combobox"], [role="menuitem"], [role="listbox"], [role="option"], a, [data-no-toggle]')) return;
        if (t.closest && t.closest('[data-testid^="alloc-"]')) return;
        onSelect?.(!selected);
      }}
      className={`p-3.5 rounded-xl border transition-all cursor-pointer
      ${selected
        ? 'border-blue-500 ring-2 ring-blue-100 bg-blue-50/40 shadow-sm'
        : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm'}`}
      data-testid={`doc-card-${doc.id}`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={selected}
          onCheckedChange={onSelect}
          className="mt-1"
          data-testid={`doc-check-${doc.id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Icon className="w-3.5 h-3.5 flex-shrink-0 text-slate-500" />
            <span className="font-medium text-sm truncate">
              {doc.doc_number || doc.counterparty || 'Untitled'}
            </span>
            <Badge className={`text-[10px] ${meta.color}`}>
              {meta.label.replace(/s$/, '')}
            </Badge>
            {closeMatch && !selected && (
              <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                <Target className="w-2.5 h-2.5 mr-0.5 inline" /> Close match
              </Badge>
            )}
            {currencyMismatch && (
              <Badge className="text-[10px] bg-yellow-100 text-yellow-800 border-yellow-200">
                FX · {doc.currency}
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1 truncate">
            {doc.counterparty} · {doc.date || '—'}
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="text-slate-500">Total</span>
            <span className="font-mono">{formatCurrency(doc.total_amount, doc.currency)}</span>
            {doc.outstanding !== undefined && doc.outstanding !== doc.total_amount && (
              <>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">Outstanding</span>
                <span className="font-mono font-medium text-blue-700">
                  {formatCurrency(doc.outstanding, doc.currency)}
                </span>
              </>
            )}
          </div>
          {doc.best_match && (
            <Badge className="text-[9px] bg-emerald-600 text-white mt-1 mr-1" data-testid={`doc-best-match-${doc.id}`}>Best match</Badge>
          )}
          {doc.doc_type === 'recorded_entry' && doc.days_apart != null && (
            <span className="text-[10px] text-slate-500 mt-1 mr-1">{doc.days_apart}d apart{Math.abs(doc.amount_gap || 0) > 0.009 ? ` · ${doc.amount_gap > 0 ? '+' : ''}${formatCurrency(doc.amount_gap, doc.currency)} vs bank` : ''}</span>
          )}
          {doc.badge && (
            <div className={`text-[11px] mt-1 ${doc.doc_type === 'recorded_entry' ? 'text-emerald-800 font-medium not-italic' : 'text-slate-500 italic'}`}
                 data-testid={`doc-badge-${doc.id}`}>
              {doc.doc_type === 'recorded_entry' && <BookCheck className="w-3 h-3 inline mr-1 -mt-0.5" />}
              {doc.badge}
            </div>
          )}
        </div>
      </div>
      {selected && (
        <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-no-toggle onClick={(e) => e.stopPropagation()}>
          <AllocationWizard doc={doc} tx={tx} value={allocation} onChange={onUpdateAlloc} scenarios={scenarios} fxRate={fxRate} />
        </div>
      )}
    </div>
  );
}


function AllocationWizard({ doc, tx, value, onChange, scenarios, fxRate }) {
  const outstanding = doc.outstanding ?? doc.total_amount ?? 0;
  const direction = tx.direction || 'outflow';
  const rawScList = (scenarios?.[doc.doc_type]?.[direction]) || [];
  // iter1428 · Filter out scenarios flagged `disabled` by the registry.
  // Disabled scenarios cover (a) silent financial leaks — cascade
  // incomplete → produces wrong depreciation/balances (fixed_asset
  // partial_disposal + add_capitalisation); (b) duplicates of the
  // GL-category route with no doc/schedule creation (prepayment
  // initial/top_up, deferred_revenue advance/top_up). Users are
  // routed to the correct per-doc page which cascades properly.
  const _scWithoutDisabled = rawScList.filter(s => !s.disabled);
  // iter1418 · FX-aware cap. The amount input is in DOC currency; the
  // bank-tx amount is in TX currency. For cross-currency links,
  // compute the tx-amount translated into doc currency so the cap
  // and seed values are in the RIGHT unit (was min-ing raw £192.23
  // against $20,000 outstanding — arithmetically nonsense).
  const _docCcy = (doc.currency || '').toUpperCase();
  const _txCcy = (tx.currency || '').toUpperCase();
  const _isCross = _docCcy && _txCcy && _docCcy !== _txCcy;
  const _fxRate = parseFloat(fxRate) || 0;
  // rate is doc→tx (docAmount * rate = txAmount).
  // Convert tx.amount back to doc-ccy: txInDocCcy = tx.amount / rate.
  const txAbs = Math.abs(tx.amount || 0);
  const txInDocCcy = (_isCross && _fxRate > 0)
    ? (txAbs / _fxRate)
    : txAbs;
  // iter1417 · Rule A (bank-feed hard control): when the bank tx amount
  // (translated to doc ccy) is strictly less than the doc's outstanding,
  // only PARTIAL-flavoured scenarios are meaningful.
  const _hideExactScenarios = txInDocCcy > 0
    && txInDocCcy < outstanding - 0.01;
  const _EXACT_KEY_BLACKLIST = new Set([
    'full', 'full_payoff', 'full_disposal', 'maturity',
  ]);
  const scList = _hideExactScenarios
    ? _scWithoutDisabled.filter(s => s.amount_rule !== 'exact'
                            && !_EXACT_KEY_BLACKLIST.has(s.key))
    : _scWithoutDisabled;
  const _autoScenarioKey = (() => {
    if (_hideExactScenarios) {
      const _partial = scList.find(s => s.key === 'partial');
      if (_partial) return _partial.key;
    }
    return scList[0]?.key ?? null;
  })();
  const [scenario, setScenario] = useState(
    value.scenario || _autoScenarioKey,
  );
  const activeSc = scList.find(s => s.key === scenario) || scList[0];
  // iter1418 · Cap the input at min(tx-in-doc-ccy, outstanding). For
  // the £192.23 tx vs $20,000 bill at rate 0.7386 that gives
  // min($260.25, $20,000) = $260.25 — matches the actual tx.
  const perDocCap = activeSc?.amount_rule === 'exact'
    ? outstanding
    : Math.max(0, Math.min(txInDocCcy || outstanding, outstanding));
  const defaultAmount = (() => {
    if (activeSc?.amount_rule === 'exact') return outstanding;
    return Math.round((perDocCap || 0) * 100) / 100;
  })();
  const [amount, setAmount] = useState(value.amount ?? defaultAmount);
  // Excess: bank line (in doc ccy) over the document's outstanding → user picks the treatment.
  const excessAmt = Math.max(0, Math.round((txInDocCcy - outstanding) * 100) / 100);
  const excessKey = `${doc.doc_type}/${direction}`;
  const excessOptions = EXCESS_OPTIONS[excessKey] || [];
  const [excessOption, setExcessOption] = useState(value.excess?.option || '');
  const [excessGl, setExcessGl] = useState(value.excess?.gl_code || '');
  const [wht, setWht] = useState(value.wht_amount ?? 0);
  const [principal, setPrincipal] = useState(value.principal_amount ?? '');
  const [interest, setInterest] = useState(value.interest_amount ?? '');
  const [costBasis, setCostBasis] = useState(value.cost_basis ?? '');
  const [disposalFrac, setDisposalFrac] = useState(value.disposal_fraction ?? '');
  const [deductible, setDeductible] = useState(value.deductible_amount ?? '');
  const [schedulePeriod, setSchedulePeriod] = useState(value.schedule_period ?? '');
  const [splitLegs, setSplitLegs] = useState(value.split_legs ?? '');

  // iter1419 · Track whether user manually edited the amount so the
  // async re-seed effect knows to skip this allocation. Clearing
  // `_auto_seeded` on the parent state prevents fxRate-late-arrival
  // from clobbering a user-typed value.
  const [userTouchedAmount, setUserTouchedAmount] = useState(false);
  useEffect(() => {
    onChange({
      ...value,
      scenario,
      amount: parseFloat(amount) || 0,
      wht_amount: parseFloat(wht) || 0,
      principal_amount: principal === '' ? undefined : parseFloat(principal),
      interest_amount: interest === '' ? undefined : parseFloat(interest),
      cost_basis: costBasis === '' ? undefined : parseFloat(costBasis),
      disposal_fraction: disposalFrac === '' ? undefined : parseFloat(disposalFrac),
      deductible_amount: deductible === '' ? undefined : parseFloat(deductible),
      schedule_period: schedulePeriod || undefined,
      split_legs: splitLegs || undefined,
      excess: (excessAmt > 0.009 && excessOption && excessOptions.length)
        ? { option: excessOption, amount: excessAmt, gl_code: excessGl || undefined }
        : undefined,
      ...(userTouchedAmount ? { _auto_seeded: false } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, amount, wht, principal, interest, costBasis, disposalFrac,
      deductible, schedulePeriod, splitLegs, userTouchedAmount, excessOption, excessGl, excessAmt]);

  useEffect(() => {
    if (activeSc?.amount_rule === 'exact') setAmount(outstanding);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario]);

  // iter1419 · Sync local `amount` state to prop when the parent
  // re-seeds (async fxRate arrival re-computes allocation.amount).
  // Only syncs when the allocation is still flagged `_auto_seeded`
  // so user-typed overrides aren't clobbered.
  useEffect(() => {
    if (value?._auto_seeded && value.amount != null
        && String(value.amount) !== String(amount)) {
      setAmount(value.amount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.amount, value?._auto_seeded]);

  const totalApplied = (parseFloat(amount) || 0) + (parseFloat(wht) || 0);
  const violatesBounded = activeSc?.amount_rule === 'bounded'
    && totalApplied > outstanding + 0.01;
  const violatesExact = activeSc?.amount_rule === 'exact'
    && Math.abs(totalApplied - outstanding) > 0.01;
  // iter1418 · Over-tx guard is now FX-aware (compare in tx-ccy).
  const totalAppliedInTxCcy = _isCross && _fxRate > 0
    ? totalApplied * _fxRate
    : totalApplied;
  const overTxAmount = totalAppliedInTxCcy > txAbs + 0.01
    && activeSc?.amount_rule !== 'unbounded';
  // iter1417 · Rule B: when tx.amount (translated) >= outstanding, a
  // "partial" scenario means the applied amount MUST be strictly LESS
  // than outstanding.
  const partialMustBeStrictlyLess = activeSc?.key === 'partial'
    && !_hideExactScenarios
    && totalApplied >= outstanding - 0.01;

  const requiredInputs = new Set((activeSc?.inputs || []).map(i => i.name));

  return (
    <div className="space-y-3">
      {scList.length > 1 && (
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-slate-500">
            How to settle this?
          </Label>
          <div className="flex flex-wrap gap-1.5 mt-1"
               data-testid={`scenario-picker-${doc.id}`}>
            {scList.map(s => (
              <button key={s.key}
                type="button"
                onClick={() => setScenario(s.key)}
                data-testid={`scenario-${doc.id}-${s.key}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-all
                  ${scenario === s.key
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'}`}
                title={s.description}>
                {s.label}
              </button>
            ))}
          </div>
          {activeSc && (
            <div className="text-[10px] text-slate-500 mt-1 italic">
              {activeSc.description}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {requiredInputs.has('amount') && (
          <div className="col-span-2">
            {/* iter1418 · Explicit currency-aware label so users can't
                confuse doc-ccy vs tx-ccy amounts. Right-hand summary
                surfaces BOTH the bank-tx amount (in tx-ccy) AND its
                doc-ccy equivalent so they see exactly what full-tx
                allocation would look like. */}
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center justify-between">
              <span>
                Applied amount
                <span className="ml-1.5 text-slate-500 normal-case tracking-normal font-mono">
                  ({doc.currency || tx.currency || 'USD'})
                </span>
                {activeSc?.key === 'partial' && (
                  <span className="ml-1.5 text-blue-600 normal-case tracking-normal">
                    · partial
                  </span>
                )}
                {activeSc?.amount_rule === 'exact' && (
                  <span className="ml-1.5 text-slate-500 normal-case tracking-normal">
                    · locked to {formatCurrency(outstanding, doc.currency || tx.currency || 'USD')}
                  </span>
                )}
              </span>
              <span className="text-[9px] text-slate-400 normal-case tracking-normal">
                Bank tx {formatCurrency(txAbs, tx.currency || 'USD')}
                {_isCross && _fxRate > 0 && (
                  <span className="text-purple-500">
                    {' '}(≈ {formatCurrency(txInDocCcy, doc.currency || 'USD')})
                  </span>
                )}
                {' · '}
                Doc outstanding {formatCurrency(outstanding, doc.currency || tx.currency || 'USD')}
              </span>
            </Label>
            <Input type="number" step="0.01"
              className={`h-8 text-xs ${(violatesBounded || violatesExact || partialMustBeStrictlyLess || overTxAmount) ? 'border-red-500' : ''}`}
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setUserTouchedAmount(true); }}
              min={0}
              max={activeSc?.amount_rule === 'unbounded' ? undefined : perDocCap}
              disabled={activeSc?.amount_rule === 'exact'}
              placeholder={
                activeSc?.key === 'partial'
                  ? `Type any amount up to ${formatCurrency(perDocCap, doc.currency || tx.currency || 'USD')}`
                  : undefined
              }
              data-testid={`alloc-amt-${doc.id}`} />
            {excessAmt > 0.009 && excessOptions.length > 0 && (
              <div className="mt-2 p-2 rounded border border-amber-300 bg-amber-50" data-testid={`alloc-excess-${doc.id}`}>
                <div className="text-[11px] font-medium text-amber-900">
                  Bank line is {formatCurrency(excessAmt, doc.currency)} more than this {DOC_META[doc.doc_type]?.label?.toLowerCase() || 'document'}. Treat the extra as:
                </div>
                <select
                  className="mt-1 w-full text-[11px] border rounded px-1.5 py-1 bg-white"
                  value={excessOption}
                  onChange={(e) => setExcessOption(e.target.value)}
                  data-testid={`alloc-excess-option-${doc.id}`}
                >
                  <option value="">Choose…</option>
                  {excessOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                {excessOptions.find(o => o.key === excessOption)?.needsGl && (
                  <Input
                    className="mt-1 h-7 text-[11px]"
                    placeholder="GL code (blank = default FX / bank charges account)"
                    value={excessGl}
                    onChange={(e) => setExcessGl(e.target.value)}
                    data-testid={`alloc-excess-gl-${doc.id}`}
                  />
                )}
              </div>
            )}
            {/* iter1418 · Live cross-currency preview: shows both the
                doc-ccy allocation AND its tx-ccy equivalent so users
                see exactly what the bank leg will receive. */}
            {_isCross && _fxRate > 0 && parseFloat(amount) > 0 && (
              <div className="text-[10px] mt-1 text-purple-700 font-mono"
                   data-testid={`alloc-fx-preview-${doc.id}`}>
                = {formatCurrency(totalAppliedInTxCcy, tx.currency || 'USD')} @ {_fxRate}
                {' '}
                <span className="text-slate-400">
                  ({_docCcy} → {_txCcy})
                </span>
              </div>
            )}
            <div className="text-[10px] mt-1"
                 data-testid={`alloc-amt-hint-${doc.id}`}>
              {partialMustBeStrictlyLess ? (
                <span className="text-red-600">
                  Partial amount must be strictly less than the outstanding
                  {' '}({formatCurrency(outstanding, doc.currency || tx.currency || 'USD')}).
                  Increase to full or reduce.
                </span>
              ) : violatesBounded ? (
                <span className="text-red-600">
                  Cannot exceed the doc outstanding
                  {' '}({formatCurrency(outstanding, doc.currency || tx.currency || 'USD')}).
                </span>
              ) : overTxAmount ? (
                <span className="text-red-600">
                  Cannot exceed the bank tx amount
                  {' '}({formatCurrency(txAbs, tx.currency || 'USD')}
                  {_isCross && _fxRate > 0 && (
                    <>
                      {' '}≈ {formatCurrency(txInDocCcy, doc.currency || 'USD')}
                    </>
                  )}).
                </span>
              ) : (
                <span className="text-slate-500">
                  Cap:{' '}
                  <span className="font-mono text-slate-700">
                    {formatCurrency(perDocCap, doc.currency || tx.currency || 'USD')}
                  </span>
                  {' '}(smaller of bank tx and doc outstanding
                  {_isCross ? `, translated at ${_fxRate}` : ''}).
                  The whole bank tx must be fully allocated across your selected docs.
                </span>
              )}
            </div>
          </div>
        )}
        {requiredInputs.has('wht_amount') && (
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">
              WHT {doc.doc_type === 'invoice' ? 'receivable' : 'payable'}
            </Label>
            <Input type="number" step="0.01" className="h-7 text-xs"
              value={wht} onChange={(e) => setWht(e.target.value)}
              data-testid={`alloc-wht-${doc.id}`} />
          </div>
        )}
        {requiredInputs.has('principal_amount') && (
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Principal</Label>
            <Input type="number" step="0.01" className="h-7 text-xs"
              value={principal} onChange={(e) => setPrincipal(e.target.value)}
              data-testid={`alloc-principal-${doc.id}`} />
          </div>
        )}
        {requiredInputs.has('interest_amount') && (
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Interest</Label>
            <Input type="number" step="0.01" className="h-7 text-xs"
              value={interest} onChange={(e) => setInterest(e.target.value)}
              data-testid={`alloc-interest-${doc.id}`} />
          </div>
        )}
        {requiredInputs.has('cost_basis') && (
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Cost basis</Label>
            <Input type="number" step="0.01" className="h-7 text-xs"
              value={costBasis} onChange={(e) => setCostBasis(e.target.value)}
              data-testid={`alloc-cost-${doc.id}`} />
          </div>
        )}
        {requiredInputs.has('disposal_fraction') && (
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Fraction disposed</Label>
            <Input type="number" step="0.01" min="0" max="1" className="h-7 text-xs"
              value={disposalFrac} onChange={(e) => setDisposalFrac(e.target.value)}
              data-testid={`alloc-disposal-${doc.id}`} />
          </div>
        )}
        {requiredInputs.has('deductible_amount') && (
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Deductible</Label>
            <Input type="number" step="0.01" className="h-7 text-xs"
              value={deductible} onChange={(e) => setDeductible(e.target.value)}
              data-testid={`alloc-deductible-${doc.id}`} />
          </div>
        )}
        {requiredInputs.has('schedule_period') && (
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Milestone</Label>
            <Input className="h-7 text-xs"
              value={schedulePeriod} onChange={(e) => setSchedulePeriod(e.target.value)}
              placeholder={`e.g. ${new Date().getFullYear()}-Q3`}
              data-testid={`alloc-milestone-${doc.id}`} />
          </div>
        )}
        {requiredInputs.has('split_legs') && (
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">
              Split legs (JSON)
            </Label>
            <Input className="h-7 text-xs font-mono"
              value={splitLegs} onChange={(e) => setSplitLegs(e.target.value)}
              placeholder='[{"gl_code":"6100","amount":80},{"gl_code":"6200","amount":20}]'
              data-testid={`alloc-splits-${doc.id}`} />
          </div>
        )}
      </div>

      {(violatesBounded || violatesExact) && (
        <div className="text-[10px] text-red-700 flex items-center gap-1"
             data-testid={`alloc-error-${doc.id}`}>
          <AlertCircle className="w-3 h-3" />
          {violatesExact
            ? `'${activeSc.label}' requires exactly ${formatCurrency(outstanding, doc.currency)}`
            : `'${activeSc.label}' cannot exceed outstanding ${formatCurrency(outstanding, doc.currency)}`}
        </div>
      )}
      {!violatesBounded && !violatesExact && overTxAmount && (
        <div className="text-[10px] text-orange-700 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Applied {formatCurrency(totalApplied, doc.currency)} exceeds bank tx {formatCurrency(Math.abs(tx.amount || 0), tx.currency)} — check the scenario.
        </div>
      )}
    </div>
  );
}


export default function SmartLinkDocumentModal({ open, onOpenChange, transaction, onLinked, isRelink = false, focusType = null }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({ groups: [] });
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTab, setSelectedTab] = useState(focusType || 'all');
  const [selected, setSelected] = useState({});
  const [scenarios, setScenarios] = useState(null);
  const [sortBy, setSortBy] = useState('score');
  const [filters, setFilters] = useState({
    amount_min: '', amount_max: '', date_from: '', date_to: '',
    currency: '', counterparty: '', tolerance_pct: '',
  });
  // iter1408 · Cross-currency FX rate input (settlement-date rate).
  const [fxRate, setFxRate] = useState('');
  const [fxRateAuto, setFxRateAuto] = useState(null);   // system-fetched
  const [fxRateLoading, setFxRateLoading] = useState(false);
  const [fxOverridden, setFxOverridden] = useState(false);
  // Several bank lines can be matched together (match group): `transaction` may be an array.
  const txList = Array.isArray(transaction) ? transaction : (transaction ? [transaction] : []);
  const tx = txList.length > 1 ? {
    ...txList[0],
    amount: txList.reduce((s, t) => s + Math.abs(t.amount || 0), 0) * ((txList[0].direction || 'outflow') === 'inflow' ? 1 : -1),
    description: `${txList.length} bank lines: ${txList.map(t => (t.description || '').slice(0, 24)).join(' · ')}`,
    _group_ids: txList.map(t => t.id),
  } : (transaction || {});

  const fetchLinkable = useCallback(async () => {
    if (!tx?.id) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filters.amount_min) params.set('amount_min', filters.amount_min);
      if (filters.amount_max) params.set('amount_max', filters.amount_max);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      if (filters.currency) params.set('currency', filters.currency);
      if (filters.counterparty) params.set('counterparty', filters.counterparty);
      if (filters.tolerance_pct) params.set('tolerance_pct', filters.tolerance_pct);
      if (sortBy && sortBy !== 'score') params.set('sort_by', sortBy);
      const r = await axios.get(
        `${API}/api/bank-feeds/linkable-documents/${tx.id}?${params}`,
        { withCredentials: true },
      );
      setData(r.data || { groups: [] });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load linkable documents');
    } finally {
      setLoading(false);
    }
  }, [tx?.id, search, filters, sortBy]);

  useEffect(() => {
    if (open && tx?.id) fetchLinkable();
    if (!open) {
      setSelected({}); setSearch(''); setSelectedTab(focusType || 'all');
      setSortBy('score');
      setFilters({ amount_min: '', amount_max: '', date_from: '', date_to: '',
                   currency: '', counterparty: '', tolerance_pct: '' });
      setFxRate(''); setFxRateAuto(null); setFxOverridden(false);
    }
  }, [open, tx?.id, fetchLinkable]);

  // iter1408 · Detect FX mismatch across the selected allocations
  // iter1420 · Use the shared tri-currency helper so rate direction
  // is picked the SAME way as the backend resolver. Also detects the
  // case-B mismatch (tx-ccy ≠ func-ccy even when doc == tx) that the
  // old logic missed.
  const fxMismatchInfo = useMemo(() => {
    if (!tx?.currency) return null;
    const funcCcy = (tx.functional_currency
                     || data?.functional_currency
                     || tx.currency).toUpperCase();
    const items = (data?.groups || []).flatMap(g => g.items);
    // Anchor on the FIRST selected doc's currency (single-rate contract).
    let docCcy = null;
    for (const alloc of Object.values(selected)) {
      const doc = items.find(d => d.id === alloc.document_id);
      if (doc?.currency) { docCcy = doc.currency.toUpperCase(); break; }
    }
    // No doc selected yet — fall back to detecting tx-vs-func alone.
    const _docForDir = docCcy || tx.currency.toUpperCase();
    const dir = pickRateDirection(_docForDir, tx.currency, funcCcy);
    if (!dir) return null;
    return { from: dir[0], to: dir[1], funcCcy, docCcy: _docForDir };
  }, [selected, data, tx.currency, tx.functional_currency]);

  useEffect(() => {
    if (!fxMismatchInfo) {
      setFxRateAuto(null);
      if (!fxOverridden) setFxRate('');
      return;
    }
    // Fetch the settlement-date FX rate. For a bank feed tx the date
    // is fixed (tx.date). User can still override the fetched rate.
    setFxRateLoading(true);
    const d = (tx.date || tx.transaction_date || '').slice(0, 10);
    const params = d ? `?date=${d}` : '';
    axios.get(
      `${API}/api/fx/rate-for-date/${fxMismatchInfo.from}/${fxMismatchInfo.to}${params}`,
      { withCredentials: true },
    ).then(r => {
      const rate = r.data?.rate;
      if (rate) {
        setFxRateAuto(rate);
        if (!fxOverridden) setFxRate(String(rate));
      }
    }).catch(() => {}).finally(() => setFxRateLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fxMismatchInfo?.from, fxMismatchInfo?.to, tx.date]);

  useEffect(() => {
    if (!open || scenarios) return;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/bank-feeds/settlement-scenarios`,
          { withCredentials: true },
        );
        setScenarios(r.data?.scenarios || {});
      } catch (e) {
        setScenarios({});
      }
    })();
  }, [open, scenarios]);

  const totalSelected = useMemo(
    () => Object.values(selected).reduce(
      (s, a) => s + (parseFloat(a.amount) || 0) + (parseFloat(a.wht_amount) || 0), 0,
    ),
    [selected],
  );

  // iter1404 Phase 4b · gate submit on per-doc bounded/exact violations.
  // The AllocationWizard renders inline errors; block submit whenever
  // any allocation is currently violating its scenario's amount_rule.
  const hasAllocError = useMemo(() => {
    if (!scenarios) return false;
    const groupsForCheck = data?.groups || [];
    const _rate = parseFloat(fxRate) || 0;
    for (const alloc of Object.values(selected)) {
      const scList = (scenarios?.[alloc.doc_type]?.[tx.direction || 'outflow']) || [];
      const sc = scList.find(s => s.key === alloc.scenario);
      if (!sc) continue;
      const doc = groupsForCheck
        .flatMap(g => g.items)
        .find(d => d.id === alloc.document_id);
      if (!doc) continue;
      const outstanding = doc.outstanding ?? doc.total_amount ?? 0;
      const applied = (parseFloat(alloc.amount) || 0)
        + (parseFloat(alloc.wht_amount) || 0);
      if (sc.amount_rule === 'bounded' && applied > outstanding + 0.01) return true;
      if (sc.amount_rule === 'exact' && Math.abs(applied - outstanding) > 0.01) return true;
      const _txAbs = Math.abs(tx.amount || 0);
      const _fullWasPossible = _txAbs > 0
        && _txAbs >= outstanding - 0.01;
      if (sc.key === 'partial' && _fullWasPossible
          && applied >= outstanding - 0.01) return true;
      // iter1418 · Per-alloc cap: never allocate more than the bank
      // tx amount TRANSLATED into the doc's currency. For same-ccy
      // the translation is identity. For cross-ccy uses fxRate.
      const _docCcy = (doc.currency || '').toUpperCase();
      const _txCcy = (tx.currency || '').toUpperCase();
      const _isCross = _docCcy && _txCcy && _docCcy !== _txCcy;
      const _txInDocCcy = (_isCross && _rate > 0)
        ? (_txAbs / _rate)
        : _txAbs;
      if (sc.amount_rule !== 'unbounded'
          && applied > _txInDocCcy + 0.01) return true;
      if (applied <= 0 && sc.key !== 'multi_leg_split') return true;
    }
    return false;
  }, [selected, scenarios, data, tx.direction, tx.amount, tx.currency, fxRate]);

  const toggleDoc = (doc) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[doc.id]) delete next[doc.id];
      else {
        // iter1419 · Smart distribution:
        //   * Compute the REMAINING un-allocated bank-tx amount.
        //   * Convert that remainder into THIS doc's currency using
        //     the current fxRate (identity for same-ccy).
        //   * Seed = min(remainder-in-doc-ccy, doc.outstanding).
        //
        //   Follows the user rule:
        //     tx == outstanding  → prefill full tx amount
        //     tx <  outstanding  → prefill full tx amount
        //     tx >  outstanding  → prefill outstanding (excess goes
        //                          to the next doc the user picks).
        const _rate = parseFloat(fxRate) || 0;
        const _docCcy = (doc.currency || '').toUpperCase();
        const _txCcy = (tx.currency || '').toUpperCase();
        const _isCross = _docCcy && _txCcy && _docCcy !== _txCcy;
        const _txAbs = Math.abs(tx.amount || 0);

        // Sum whatever's already allocated (in tx-ccy) so we know the
        // un-consumed bank-tx portion available to this doc.
        let _consumedTxCcy = 0;
        for (const a of Object.values(prev)) {
          const _amt = (parseFloat(a.amount) || 0)
            + (parseFloat(a.wht_amount) || 0);
          const _otherDoc = (data?.groups || [])
            .flatMap(g => g.items)
            .find(d => d.id === a.document_id);
          const _oDocCcy = (_otherDoc?.currency || '').toUpperCase();
          const _oIsCross = _oDocCcy && _txCcy && _oDocCcy !== _txCcy;
          _consumedTxCcy += _oIsCross && _rate > 0
            ? _amt * _rate
            : _amt;
        }
        const _remainingTxCcy = Math.max(0, _txAbs - _consumedTxCcy);
        const _remainingInDocCcy = (_isCross && _rate > 0)
          ? (_remainingTxCcy / _rate)
          : _remainingTxCcy;
        const _outstanding = doc.outstanding ?? doc.total_amount ?? 0;
        let _seedAmount = _remainingInDocCcy > 0
          ? Math.round(Math.min(_remainingInDocCcy, _outstanding) * 100) / 100
          : _outstanding;
        // G6 · WHT seeded ONLY from the document's own WHT choice (made at creation):
        //   cash == outstanding − remaining WHT  → full remaining WHT;  partial → pro-rata;  no WHT on the doc → 0.
        let _seedWht = 0;
        const _docWhtLeft = Math.max(0, (parseFloat(doc.wht_amount) || 0) - (parseFloat(doc.wht_recognised) || 0));
        if (doc.wht_expected && _docWhtLeft > 0 && _outstanding > 0) {
          const _netDue = Math.round((_outstanding - _docWhtLeft) * 100) / 100;
          if (Math.abs(_remainingInDocCcy - _netDue) <= 0.05) {
            _seedWht = _docWhtLeft; _seedAmount = _netDue;
          } else if (_remainingInDocCcy > 0 && _remainingInDocCcy < _netDue) {
            _seedWht = Math.round(_docWhtLeft * (_remainingInDocCcy / _netDue) * 100) / 100;
            _seedAmount = Math.round(_remainingInDocCcy * 100) / 100;
          }
        }
        next[doc.id] = {
          document_id: doc.id, doc_type: doc.doc_type,
          amount: _seedAmount,
          wht_amount: _seedWht,
          wht_seeded_from_document: _seedWht > 0,
          gl_code: doc.gl_code,
          // iter1419 · Tag so the async re-seed effect can identify
          // allocations that need to be recomputed once fxRate loads.
          _auto_seeded: true,
        };
      }
      return next;
    });
  };

  // iter1419 · Async-rate re-seed. When fxRate is fetched AFTER the
  // user has already toggled a doc (which is common because the rate
  // is loaded from /api/fx/rate-for-date/... on modal open), the
  // initial seed was computed with rate=0 → identity mapping. As soon
  // as the rate arrives, walk every allocation still flagged
  // `_auto_seeded` and re-compute using the smart-distribution rule.
  useEffect(() => {
    const _rate = parseFloat(fxRate) || 0;
    if (_rate <= 0) return;
    setSelected(prev => {
      const items = (data?.groups || []).flatMap(g => g.items);
      const _txCcy = (tx.currency || '').toUpperCase();
      const _txAbs = Math.abs(tx.amount || 0);
      // Walk the current allocations in a stable order and re-seed
      // any auto-seeded ones using remaining budget.
      const ids = Object.keys(prev);
      let _consumed = 0;
      const next = { ...prev };
      for (const id of ids) {
        const a = prev[id];
        const doc = items.find(d => d.id === id);
        if (!doc) continue;
        const _docCcy = (doc.currency || '').toUpperCase();
        const _isCross = _docCcy && _txCcy && _docCcy !== _txCcy;
        if (a._auto_seeded) {
          const _remainingTxCcy = Math.max(0, _txAbs - _consumed);
          const _remainingInDocCcy = _isCross
            ? _remainingTxCcy / _rate
            : _remainingTxCcy;
          const _outstanding = doc.outstanding ?? doc.total_amount ?? 0;
          const _seed = _remainingInDocCcy > 0
            ? Math.round(Math.min(_remainingInDocCcy, _outstanding) * 100) / 100
            : _outstanding;
          next[id] = { ...a, amount: _seed, _auto_seeded: false };
          _consumed += _isCross ? _seed * _rate : _seed;
        } else {
          const _amt = (parseFloat(a.amount) || 0)
            + (parseFloat(a.wht_amount) || 0);
          _consumed += _isCross ? _amt * _rate : _amt;
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fxRate]);

  const submit = async () => {
    if (!Object.keys(selected).length) {
      toast.error('Select at least one document to link.');
      return;
    }
    // iter1408 · Require FX rate when any allocation is cross-currency.
    if (fxMismatchInfo && !parseFloat(fxRate)) {
      toast.error(`Enter a ${fxMismatchInfo.from} → ${fxMismatchInfo.to} FX rate to continue.`);
      return;
    }
    setSubmitting(true);
    try {
      const body = tx._group_ids
        ? { transaction_ids: tx._group_ids, allocations: Object.values(selected) }
        : { transaction_id: tx.id, allocations: Object.values(selected) };
      if (fxMismatchInfo && parseFloat(fxRate)) {
        body.exchange_rate = parseFloat(fxRate);
      }
      // iter1411 · In-Books relink runs teardown + fresh compose in one
      // atomic call. Same request/response shape as /link-and-settle so
      // the response-parsing block below is unchanged.
      const endpoint = tx._group_ids
        ? `${API}/api/bank-feeds/link-and-settle-group`
        : (isRelink
          ? `${API}/api/bank-feeds/relink-and-settle/${tx.id}`
          : `${API}/api/bank-feeds/link-and-settle`);
      const r = await axios.post(endpoint, body, { withCredentials: true });
      const kind = r.data.residual_kind;
      const acct = r.data.residual_account?.description
        || r.data.residual_account?.code;
      let msg = r.data.match_only
        ? (r.data.message || 'Matched to the entry already in your books - no duplicate posted.')
        : tx._group_ids
          ? `Matched ${tx._group_ids.length} bank lines as one group to ${Object.keys(selected).length} document(s).`
          : isRelink
            ? `Prior linkage cleared and ${Object.keys(selected).length} document(s) relinked.`
            : `Settled ${Object.keys(selected).length} document(s).`;
      if (kind === 'fx' && acct) msg += ` FX difference booked to ${acct}.`;
      toast.success(msg);
      onLinked?.(r.data);
      onOpenChange(false);
    } catch (e) {
      // iter1408/1411 · Detail may be a plain string OR a structured dict
      // ({ error, fx_rate_required?, teardown_completed?, hint? }). Prefer
      // .error over raw JSON, and surface hint on cleared-linkage errors.
      const detail = e?.response?.data?.detail;
      let msg;
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object') {
        msg = detail.error || JSON.stringify(detail);
        if (detail.hint) msg += ` — ${detail.hint}`;
      } else msg = e?.message || 'Settlement failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // focusType (special-item flows): only that family is offered.
  const groups = focusType ? (data.groups || []).filter(g => g.type === focusType) : (data.groups || []);
  const visibleGroups = selectedTab === 'all'
    ? groups
    : groups.filter(g => g.type === selectedTab);

  // Derive currencies for filter dropdown
  const availableCurrencies = useMemo(() => {
    const set = new Set();
    for (const g of groups) for (const it of g.items) if (it.currency) set.add(it.currency);
    return Array.from(set).sort();
  }, [groups]);

  // Active filter chips
  const activeChips = [];
  if (filters.amount_min || filters.amount_max) {
    activeChips.push({
      key: 'amt',
      icon: DollarSign,
      label: 'Amount',
      value: `${filters.amount_min || '0'} – ${filters.amount_max || '∞'}`,
      onClear: () => setFilters({ ...filters, amount_min: '', amount_max: '' }),
    });
  }
  if (filters.date_from || filters.date_to) {
    activeChips.push({
      key: 'date',
      icon: Calendar,
      label: 'Date',
      value: `${filters.date_from || '…'} → ${filters.date_to || '…'}`,
      onClear: () => setFilters({ ...filters, date_from: '', date_to: '' }),
    });
  }
  if (filters.currency) {
    activeChips.push({
      key: 'ccy',
      icon: Coins,
      label: 'Currency',
      value: filters.currency,
      onClear: () => setFilters({ ...filters, currency: '' }),
    });
  }
  if (filters.counterparty) {
    activeChips.push({
      key: 'cp',
      icon: Users2,
      label: 'Counterparty',
      value: filters.counterparty,
      onClear: () => setFilters({ ...filters, counterparty: '' }),
    });
  }
  if (filters.tolerance_pct) {
    activeChips.push({
      key: 'tol',
      icon: Target,
      label: 'Tolerance',
      value: `± ${filters.tolerance_pct}%`,
      onClear: () => setFilters({ ...filters, tolerance_pct: '' }),
    });
  }

  const txAmt = Math.abs(tx.amount || 0);
  // iter1414 · FX-aware match rail. Backend hard-rejects unless
  // sum(allocations in tx-ccy) == tx.amount. When docs are in a
  // different currency than the bank tx, convert via exchange_rate
  // for the comparison (each doc contributes doc_amount * fxRate).
  const _fxRate = parseFloat(fxRate) || 1;
  const totalSelectedInTxCcy = useMemo(() => {
    let sum = 0;
    for (const a of Object.values(selected)) {
      const _amt = (parseFloat(a.amount) || 0)
        + (parseFloat(a.wht_amount) || 0)
        + (parseFloat(a.excess?.amount) || 0);
      const _doc = (data?.groups || [])
        .flatMap(g => g.items)
        .find(d => d.id === a.document_id);
      const _docCcy = (_doc?.currency || tx.currency || '').toUpperCase();
      const _txCcy = (tx.currency || '').toUpperCase();
      const _isCross = _docCcy && _txCcy && _docCcy !== _txCcy;
      sum += _isCross ? _amt * _fxRate : _amt;
    }
    return sum;
  }, [selected, data, tx.currency, _fxRate]);
  const matchPctRaw = txAmt > 0 ? (totalSelectedInTxCcy / txAmt) * 100 : 0;
  const matchPct = Math.min(100, matchPctRaw);
  const overAllocated = matchPctRaw > 100;
  const diff = totalSelectedInTxCcy - (tx.amount || 0);
  const matchIsExact = Math.abs(diff) < 0.01 && totalSelectedInTxCcy > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="smart-link-modal">
        {/* ─── Header ─────────────────────────────────────────── */}
        <DialogHeader className={`px-6 pt-5 pb-4 border-b ${isRelink
          ? 'bg-gradient-to-b from-amber-50/80 to-transparent'
          : 'bg-gradient-to-b from-blue-50/70 to-transparent'}`}>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isRelink ? 'bg-amber-100' : 'bg-blue-100'}`}>
              <Link2 className={`w-4 h-4 ${isRelink ? 'text-amber-700' : 'text-blue-600'}`} />
            </div>
            {isRelink ? 'Replace linkage — In Books' : 'Link this transaction'}
          </DialogTitle>
          <DialogDescription className="text-xs mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge className={`text-[10px] ${tx.direction === 'inflow'
              ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
              : 'bg-orange-100 text-orange-700 border-orange-200'}`}>
              {tx.direction === 'inflow' ? 'Money received' : 'Money paid'}
            </Badge>
            <span className="font-mono font-medium text-slate-700">
              {formatCurrency(tx.amount, tx.currency || 'USD')}
            </span>
            <span className="text-slate-400">·</span>
            <span className="italic text-slate-600 truncate max-w-md">
              {(tx.description || '').slice(0, 80)}
            </span>
          </DialogDescription>
          {/* iter1411 · In-Books relink warning banner. The prior journal
              AND any auto-created doc will be hard-deleted; user-entered
              docs will be unlinked (not deleted) then re-linked to the
              new selection. */}
          {isRelink && (
            <div
              data-testid="sl-relink-warning"
              className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50/80 px-3 py-2 text-[11px] text-amber-900">
              <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-900 font-semibold">!</span>
              <div>
                <div className="font-semibold text-amber-900">
                  This transaction is already in books.
                </div>
                <div className="mt-0.5 leading-relaxed">
                  Confirming a new linkage will delete the current journal,
                  unlink the current document(s), and post fresh. Any
                  auto-created document will be removed; user-entered
                  documents will be preserved but unlinked. Schedules
                  (loan / prepayment / deferred revenue) tied to the
                  removed doc will be regenerated on the new linkage.
                </div>
              </div>
            </div>
          )}
        </DialogHeader>

        {/* ─── Search + Filter + Sort ─────────────────────────── */}
        <div className="px-6 py-3 border-b bg-white space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                placeholder="Search reference, counterparty, journal number…"
                className="h-8 pl-8 text-xs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchLinkable()}
                data-testid="smart-link-search"
              />
            </div>
            <FilterPopover filters={filters} onChange={setFilters}
                           currencies={availableCurrencies} />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-8 text-xs gap-1 w-[168px]" data-testid="sl-sort">
                <ArrowUpDown className="w-3.5 h-3.5" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="score">Best match</SelectItem>
                <SelectItem value="amount">Amount (low → high)</SelectItem>
                <SelectItem value="date">Date (newest first)</SelectItem>
                <SelectItem value="counterparty">Counterparty (A→Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {activeChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="sl-filter-chips">
              {activeChips.map(c => (
                <Chip key={c.key} icon={c.icon} label={c.label} value={c.value}
                      onClear={c.onClear} testId={`sl-chip-${c.key}`} />
              ))}
              {activeChips.length > 1 && (
                <button
                  onClick={() => setFilters({ amount_min:'', amount_max:'', date_from:'',
                                              date_to:'', currency:'', counterparty:'', tolerance_pct:'' })}
                  className="text-[11px] text-slate-500 hover:text-slate-800 underline underline-offset-2"
                  data-testid="sl-clear-all-filters">
                  Clear all
                </button>
              )}
            </div>
          )}
          {/* iter1408 · Settlement-date FX rate. Shown only when any
              selected allocation's currency differs from the bank tx
              currency. Auto-fetched from /api/fx/rate-for-date/... on
              the tx date; user can override (setting `fxOverridden`
              stops the auto-refetch from clobbering their value). */}
          {fxMismatchInfo && (
            <div
              className="flex flex-wrap items-center gap-2 p-2 rounded-md border border-purple-200 bg-purple-50/60"
              data-testid="sl-fx-row">
              <Coins className="w-3.5 h-3.5 text-purple-700" />
              <span className="text-[11px] text-purple-900">
                FX rate <span className="font-mono">{fxMismatchInfo.from} → {fxMismatchInfo.to}</span> on{' '}
                <span className="font-mono">{(tx.date || '').slice(0, 10) || 'tx date'}</span>
              </span>
              <Input
                type="number" step="0.000001" min="0"
                className="h-7 w-32 text-xs font-mono"
                value={fxRate}
                onChange={(e) => { setFxRate(e.target.value); setFxOverridden(true); }}
                placeholder={fxRateLoading ? 'Loading…' : '0.000000'}
                data-testid="sl-fx-rate-input"
              />
              {fxRateAuto && (
                <>
                  {fxOverridden && parseFloat(fxRate) !== fxRateAuto && (
                    <button
                      type="button"
                      onClick={() => { setFxRate(String(fxRateAuto)); setFxOverridden(false); }}
                      className="text-[10px] text-purple-700 hover:text-purple-950 underline underline-offset-2"
                      data-testid="sl-fx-reset-auto">
                      Reset to system rate ({fxRateAuto})
                    </button>
                  )}
                  {(!fxOverridden || parseFloat(fxRate) === fxRateAuto) && (
                    <span className="text-[10px] text-purple-700 italic">
                      system rate; override any time
                    </span>
                  )}
                </>
              )}
              {/* iter1408 · Live conversion preview across mismatched
                  allocations. iter1428 · Correctness FIX: the previous
                  preview hard-assumed `tx.currency == fxMismatchInfo.to`
                  and always divided by rate. But when `docCcy == txCcy`
                  (no doc selected yet, or same-ccy doc), the direction
                  is `[tx, func]` — so `tx.currency` is actually the
                  FROM side, not TO. That mis-labelling produced the
                  screenshot bug where a £74.83 GBP tx was displayed
                  as "≈ 74.83 USD → 55.25 GBP" — reading the modal as
                  if the source were USD when it was really GBP.
                  Now: figure out which side of the rate tx.currency
                  is on, and label + convert accordingly. */}
              {parseFloat(fxRate) > 0 && (() => {
                const _rate = parseFloat(fxRate);
                if (_rate <= 0) return null;
                const _txAbs = Math.abs(tx.amount || 0);
                const _txCcyU = (tx.currency || '').toUpperCase();
                const _from = fxMismatchInfo.from;
                const _to   = fxMismatchInfo.to;
                // Rate is from → to. Multiply from-ccy amount to get
                // to-ccy amount. If tx is on the from side, multiply;
                // otherwise divide.
                const _txIsFrom = _txCcyU === _from;
                const _otherAmount = _txIsFrom
                  ? _txAbs * _rate
                  : _txAbs / _rate;
                const _srcCcy = _txIsFrom ? _from : _to;
                const _dstCcy = _txIsFrom ? _to   : _from;
                return (
                  <span
                    className="text-[10px] text-purple-900 font-mono ml-auto"
                    data-testid="sl-fx-preview">
                    ≈ {_txAbs.toLocaleString(undefined,{maximumFractionDigits:2})} {_srcCcy} → {_otherAmount.toLocaleString(undefined,{maximumFractionDigits:2})} {_dstCcy}
                  </span>
                );
              })()}
            </div>
          )}
        </div>

        {/* ─── Body ───────────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <Tabs value={selectedTab} onValueChange={setSelectedTab}
                className="flex-1 flex flex-col overflow-hidden">
            <div className="px-6 pt-3 pb-2 border-b bg-slate-50/60 overflow-x-auto shrink-0">
              <TabsList className="h-auto flex flex-nowrap justify-start gap-1 bg-transparent p-0 w-max">
                {!focusType && (
                <TabsTrigger value="all"
                  className="h-7 text-[11px] whitespace-nowrap data-[state=active]:bg-blue-50"
                  data-testid="tab-all">
                  All ({data.total_items ?? 0})
                </TabsTrigger>
                )}
                {focusType && groups.length === 0 && !loading && (
                  <span className="text-[11px] text-slate-500" data-testid="sl-focus-empty">
                    No open {DOC_META[focusType]?.label?.toLowerCase() || focusType} to match — close and choose "Track as new item".
                  </span>
                )}
                {groups.map(g => (
                  <TabsTrigger key={g.type} value={g.type}
                    className="h-7 text-[11px] whitespace-nowrap data-[state=active]:bg-blue-50"
                    data-testid={`tab-${g.type}`}>
                    {DOC_META[g.type]?.label || g.type} ({g.count})
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <TabsContent value={selectedTab}
              className="flex-1 overflow-y-auto px-6 py-4 space-y-3 mt-0 bg-slate-50/30">
              {loading && (
                <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading open documents…
                </div>
              )}
              {error && (
                <div className="flex items-center gap-2 text-sm text-red-700 p-3 bg-red-50 border border-red-200 rounded">
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}
              {!loading && !visibleGroups.length && (
                <div className="text-center py-16 text-slate-500 text-sm">
                  {activeChips.length > 0
                    ? 'No documents match your filters. Try clearing some.'
                    : 'No open documents found for this transaction.'}
                </div>
              )}
              {visibleGroups.map(g => (
                <div key={g.type} className="space-y-2">
                  {selectedTab === 'all' && (
                    <div className="flex items-center gap-2 pt-1">
                      <div className="text-[11px] font-medium text-slate-600 uppercase tracking-wide">
                        {DOC_META[g.type]?.label || g.type}
                      </div>
                      <Badge variant="outline" className="text-[10px]">{g.count}</Badge>
                    </div>
                  )}
                  {g.items.map(doc => (
                    <DocCard
                      key={doc.id}
                      doc={doc}
                      tx={tx}
                      selected={!!selected[doc.id]}
                      allocation={selected[doc.id] || { amount: doc.outstanding, wht_amount: 0 }}
                      onSelect={() => toggleDoc(doc)}
                      onUpdateAlloc={(alloc) => setSelected(prev => ({ ...prev, [doc.id]: alloc }))}
                      scenarios={scenarios}
                      fxRate={fxRate}
                    />
                  ))}
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>

        {/* ─── Footer with match rail ────────────────────────── */}
        <DialogFooter className="px-6 py-3 border-t bg-white flex-col items-stretch gap-3 sm:flex-col">
          {Object.keys(selected).length > 0 && (
            <div className="space-y-1.5" data-testid="sl-match-rail">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-600">
                  <span className="font-medium">{Object.keys(selected).length}</span> selected · Total{' '}
                  <span className="font-mono font-medium text-slate-800">
                    {formatCurrency(totalSelectedInTxCcy, tx.currency || 'USD')}
                  </span>
                </span>
                <span className={matchIsExact
                  ? 'text-emerald-700 font-medium'
                  : Math.abs(diff) > 0.01
                    ? 'text-red-700 font-medium'
                    : 'text-slate-500'}>
                  {matchIsExact
                    ? 'Matches transaction exactly'
                    : diff > 0
                      ? `Over-allocated by ${formatCurrency(Math.abs(diff), tx.currency || 'USD')} — reduce amounts`
                      : `Short by ${formatCurrency(Math.abs(diff), tx.currency || 'USD')} — add more documents`}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all
                    ${matchIsExact ? 'bg-emerald-500'
                      : overAllocated ? 'bg-orange-500' : 'bg-blue-500'}`}
                  style={{ width: `${Math.min(100, matchPct)}%` }}
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
              disabled={submitting} data-testid="smart-link-cancel">
              Cancel
            </Button>
            <Button size="sm" onClick={submit}
              disabled={submitting || !Object.keys(selected).length || hasAllocError || !matchIsExact}
              title={
                hasAllocError
                  ? 'Fix allocation errors before submitting'
                  : (!matchIsExact
                    ? 'Total allocated must exactly equal the bank transaction amount'
                    : undefined)
              }
              className={isRelink
                ? 'bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60'
                : 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60'}
              data-testid="smart-link-submit">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Link2 className="w-3.5 h-3.5 mr-1" />}
              {isRelink
                ? `Confirm & replace linkage (${Object.keys(selected).length})`
                : `Link & Settle (${Object.keys(selected).length})`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
